import * as CL from '@design-system/component-library';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { useDebounce } from '../../common/hooks/useDebounce.js';
import { showMoreMsg, t } from '../../common/i18n/index.js';
import { createFilterEvent, createSortEvent } from '../../common/utils/analyticsUtils.js';
import { Grid } from '../Grid/Grid';
import { filterFn } from './ProductGridFilter.js';
import { ProductGridHeader } from './ProductGridHeader.js';
import { ProductGridItems } from './ProductGridItems.js';
import { searchFn } from './ProductGridSearch.js';
import { sortFn } from './ProductGridSort.js';
import { ProductGridTitle } from './ProductGridTitle.js';
import { getFiltersFromUrlSearchParams, getLocationFromQueryParams, parseQueryParams } from './ProductGridUrlUtils.js';
import type { Filters } from './ProductGridFilter.js';
import type { SortType } from './ProductGridSort.js';
import type { CatalogProduct } from '../../common/utils/catalogUtils.js';
import type { OnlineModelCategory } from '../../generated/api/models.js';
import type { ECommerceEvents } from '../PublicPage/types.js';

import './ProductGrid.scss';

export interface ProductGridProps {
  category: OnlineModelCategory;
  eCommerceEvents?: ECommerceEvents;
  items: ProductGridItem[];
  onAnalyticsEvent?: (eventData: object) => void;
  itemLimit?: number;
  title?: string;
  titleTag?: string;
  content?: string;
  id?: string;
  inEOE?: boolean;
  setVisibleProductCodes?: (codes: string[]) => void;
}

export type ProductGridItem = CatalogProduct;

export const ProductGrid = (props: ProductGridProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { category, eCommerceEvents, items, onAnalyticsEvent, itemLimit, id, inEOE } = props;
  // This exists to enable filtering in multiple product grids on the same page and remember their settings
  const urlParameterPrefix = id ?? '';
  const itemsPerScroll = itemLimit || 8;
  const urlSearchParams = new URLSearchParams(location.search);
  const [itemsToDisplay, setItemsToDisplay] = useState(itemsPerScroll);
  const debouncedNavigate = useDebounce(url => navigate(url, { replace: true }), 1000);
  const [productGridItems, setProductGridItems] = useState<ProductGridItem[]>(() => {
    const params = parseQueryParams(items, category, urlSearchParams, urlParameterPrefix);
    return items.filter(searchFn(params.search)).filter(filterFn(params.filters)).sort(sortFn(params.sort));
  });

  const [currentParams, setCurrentParams] = useState(
    parseQueryParams(items, category, urlSearchParams, urlParameterPrefix)
  );

  const currentFilters = getFiltersFromUrlSearchParams(items, category, urlSearchParams, urlParameterPrefix);

  const fetchMoreItems = () => {
    if (itemsToDisplay <= productGridItems.length) {
      setItemsToDisplay(itemsToDisplay + itemsPerScroll);
    }
  };

  const hasMoreItems = () => {
    return itemsToDisplay < productGridItems.length;
  };

  const getSlicedItems = () => {
    return productGridItems.slice(0, itemsToDisplay);
  };

  const handleSortChange = (sortType: SortType) => {
    const params = parseQueryParams(items, category, urlSearchParams, urlParameterPrefix);
    params.sort = sortType;
    setCurrentParams(params);
    debouncedNavigate(getLocationFromQueryParams(params, urlParameterPrefix));
  };

  const handleFilterChange = (filter: Filters) => {
    const params = parseQueryParams(items, category, urlSearchParams, urlParameterPrefix);
    params.filters = filter;
    setCurrentParams(params);
    // Using debouncedNavigate here would show active filter tags on the page with delay, not ideal for UX
    navigate(getLocationFromQueryParams(params, urlParameterPrefix), { replace: true });
  };

  const handleSearchChange = (searchValue: string) => {
    const params = parseQueryParams(items, category, urlSearchParams, urlParameterPrefix);
    params.search = searchValue;
    setCurrentParams(params);
    debouncedNavigate(getLocationFromQueryParams(params, urlParameterPrefix));
  };

  useEffect(() => {
    setProductGridItems(
      items
        .filter(searchFn(currentParams.search))
        .filter(filterFn(currentParams.filters))
        .sort(sortFn(currentParams.sort))
    );
    setItemsToDisplay(itemsPerScroll);
  }, [currentParams, items, category, itemsPerScroll]);

  return (
    <div className="of-product-grid">
      <Grid colWidthL={10}>
        <ProductGridTitle {...props} />

        <ProductGridHeader
          filters={currentFilters}
          search={currentParams.search}
          sort={currentParams.sort}
          setFilters={(filter: Filters) => {
            if (onAnalyticsEvent) {
              onAnalyticsEvent(createFilterEvent(filter));
            }
            handleFilterChange(filter);
          }}
          setSearch={handleSearchChange}
          setSort={(sortType: SortType) => {
            if (onAnalyticsEvent) {
              onAnalyticsEvent(createSortEvent(sortType));
            }
            handleSortChange(sortType);
          }}
        />
      </Grid>
      {/* TODO: We have CL.ProductGrid also which should be used instead of this.
                Things to consider before doing the refactor:
                  - what is the current DS version used? Migration to DS 4.x.x should be done
                  - check differences in data model used:
                      CatalogProduct vs. CL.ProductGridProduct
      */}
      <ProductGridItems inEOE={inEOE} eCommerceEvents={eCommerceEvents} productGridItems={getSlicedItems()} />
      {hasMoreItems() && (
        <div className={dsClass.TEXT_ALIGN_CENTER}>
          <CL.Button
            size="l"
            onClick={fetchMoreItems}
            color="light"
            className={`${dsClass.MARGIN_TOP_4} ${dsClass.MARGIN_BOTTOM_4}`}
          >
            {t.H6OA(showMoreMsg)}
          </CL.Button>
        </div>
      )}
    </div>
  );
};
